import {
  type InputHTMLAttributes,
  type ReactNode,
  type RefObject,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isToday,
  isValid,
  parseISO,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns';
import { CalendarDays, ChevronLeft, ChevronRight, Clock, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { nowDateTimeLocalInTimeZone } from '../../lib/timezones';

/* -------------------------------------------------------------------------- */
/* Shared helpers                                                             */
/* -------------------------------------------------------------------------- */

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

/** Normalises `2026-10-19`, `2026-10-19T14:30` and ISO timestamps to `yyyy-MM-dd`. */
export function toDateInputValue(value?: string | null): string {
  if (!value) return '';
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(String(value));
  return match ? match[1] : '';
}

/** Normalises a value to `yyyy-MM`. */
export function toMonthInputValue(value?: string | null): string {
  if (!value) return '';
  const match = /^(\d{4}-\d{2})/.exec(String(value));
  return match ? match[1] : '';
}

/** Normalises a value to `HH:mm`. */
export function toTimeInputValue(value?: string | null): string {
  if (!value) return '';
  const match = /(\d{2}):(\d{2})/.exec(String(value));
  return match ? `${match[1]}:${match[2]}` : '';
}

function parseDate(value?: string): Date | null {
  const iso = toDateInputValue(value);
  if (!iso) return null;
  const date = parseISO(iso);
  return isValid(date) ? date : null;
}

function parseMonth(value?: string): Date | null {
  const month = toMonthInputValue(value);
  if (!month) return null;
  const date = parseISO(`${month}-01`);
  return isValid(date) ? date : null;
}

function formatDateLabel(value?: string): string {
  const date = parseDate(value);
  return date ? format(date, 'd/M/yyyy') : '';
}

function formatMonthLabel(value?: string): string {
  const date = parseMonth(value);
  return date ? format(date, 'M/yyyy') : '';
}

function formatTimeLabel(value?: string): string {
  const [h, m] = toTimeInputValue(value).split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return '';
  const hour12 = h % 12 || 12;
  return `${String(hour12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

function formatDateTimeLabel(value?: string): string {
  const date = toDateInputValue(value);
  const time = toTimeInputValue(String(value ?? '').split('T')[1] ?? '');
  if (!date && !time) return '';
  if (!date) return formatTimeLabel(time);
  return `${formatDateLabel(date)}${time ? `, ${formatTimeLabel(time)}` : ''}`;
}

/* -------------------------------------------------------------------------- */
/* Manual (typed) entry                                                       */
/* -------------------------------------------------------------------------- */

const MONTH_NAMES = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

function pad2(value: number) {
  return String(value).padStart(2, '0');
}

function monthNumber(name: string): number | null {
  const index = MONTH_NAMES.indexOf(name.slice(0, 3).toLowerCase());
  return index === -1 ? null : index + 1;
}

function expandYear(value: string): number {
  const year = Number(value);
  if (value.length <= 2) return year >= 70 ? 1900 + year : 2000 + year;
  return year;
}

function buildDate(year: number, month: number, day: number): string | null {
  if (!Number.isInteger(year) || year < 1000 || year > 9999) return null;
  if (!Number.isInteger(month) || month < 1 || month > 12) return null;
  if (!Number.isInteger(day) || day < 1 || day > 31) return null;
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

/**
 * Parses manually typed date text into `yyyy-MM-dd`.
 * Accepts `19/10/2026`, `19-10-2026`, `2026-10-19`, `19 Oct 2026`,
 * `Oct 19, 2026` and compact 8-digit forms such as `19102026`.
 */
export function parseTypedDate(text: string): string | null {
  const value = text.trim().replace(/\s+/g, ' ');
  if (!value) return null;

  let match = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/.exec(value);
  if (match) return buildDate(Number(match[1]), Number(match[2]), Number(match[3]));

  match = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2,4})$/.exec(value);
  if (match) return buildDate(expandYear(match[3]), Number(match[2]), Number(match[1]));

  match = /^(\d{1,2})[\s/-]+([A-Za-z]{3,9})[\s,/-]+(\d{2,4})$/.exec(value);
  if (match) {
    const month = monthNumber(match[2]);
    if (month) return buildDate(expandYear(match[3]), month, Number(match[1]));
  }

  match = /^([A-Za-z]{3,9})[\s/-]+(\d{1,2})[\s,/-]+(\d{2,4})$/.exec(value);
  if (match) {
    const month = monthNumber(match[1]);
    if (month) return buildDate(expandYear(match[3]), month, Number(match[2]));
  }

  if (/^\d{8}$/.test(value)) {
    return (
      buildDate(Number(value.slice(0, 4)), Number(value.slice(4, 6)), Number(value.slice(6, 8))) ??
      buildDate(Number(value.slice(4, 8)), Number(value.slice(2, 4)), Number(value.slice(0, 2)))
    );
  }

  return null;
}

function assembleTime(hour: number, minute: number, period: string): string | null {
  if (!Number.isInteger(hour) || !Number.isInteger(minute)) return null;
  if (minute < 0 || minute > 59) return null;
  if (period) {
    if (hour < 1 || hour > 12) return null;
    const hour24 = (hour % 12) + (period === 'pm' ? 12 : 0);
    return `${pad2(hour24)}:${pad2(minute)}`;
  }
  if (hour < 0 || hour > 23) return null;
  return `${pad2(hour)}:${pad2(minute)}`;
}

/** Parses manually typed time text (`HH:mm`, `h:mm AM`, `830`, …) into `HH:mm`. */
export function parseTypedTime(text: string): string | null {
  const value = text.trim().replace(/\./g, '').replace(/\s+/g, ' ').toLowerCase();
  if (!value) return null;

  const periodMatch = /([ap]m?)$/.exec(value);
  const period = periodMatch ? periodMatch[1] : '';
  const body = (periodMatch ? value.slice(0, periodMatch.index) : value).trim();

  let match = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(body);
  if (match) return assembleTime(Number(match[1]), Number(match[2]), period);

  match = /^(\d{1,2})$/.exec(body);
  if (match) return assembleTime(Number(match[1]), 0, period);

  match = /^(\d{3,4})$/.exec(body);
  if (match) {
    const digits = match[1];
    return assembleTime(Number(digits.slice(0, -2)), Number(digits.slice(-2)), period);
  }

  return null;
}

/**
 * Parses typed date/time text. Returns `yyyy-MM-ddTHH:mm` when both parts are
 * present, or `yyyy-MM-dd` when only a date was typed.
 */
export function parseTypedDateTime(text: string): string | null {
  const value = text.trim();
  if (!value) return null;

  // A trailing time may be 24-hour (`02:15`) or 12-hour (`2:15 pm`). The
  // AM/PM suffix is optional: the mask and placeholder both use `hh:mm`.
  // Require the colon (or an explicit AM/PM) so a date-only value such as
  // `10/11/2020` can never be read as `10/11/20` + `20:00`.
  const timeAtEnd = /(\d{1,2}:\d{2}(?::\d{2})?\s*(?:[ap]m?)?|\d{1,2}\s*[ap]m)$/i.exec(value);
  if (timeAtEnd) {
    const dateText = value.slice(0, timeAtEnd.index).replace(/[\s,]+$/, '');
    const date = parseTypedDate(dateText);
    const time = parseTypedTime(timeAtEnd[1]);
    if (date && time) return `${date}T${time}`;
  }

  const timeAtStart = /^(\d{1,2}:\d{2}(?::\d{2})?\s*(?:[ap]m?)?|\d{1,2}\s*[ap]m)[\s,]+(.+)$/i.exec(value);
  if (timeAtStart) {
    const time = parseTypedTime(timeAtStart[1]);
    const date = parseTypedDate(timeAtStart[2]);
    if (date && time) return `${date}T${time}`;
  }

  return parseTypedDate(value);
}

/* -------------------------------------------------------------------------- */
/* Input masks (auto-format while typing)                                     */
/* -------------------------------------------------------------------------- */

/** Groups typed digits into `dd/mm/yy` (extends to `dd/mm/yyyy` past 6 digits). */
function maskDateText(raw: string): string {
  if (/[A-Za-z]/.test(raw)) return raw;

  // Respect separators the user types so single-digit day/month works and
  // pasted `dd-mm-yyyy` / `yyyy-mm-dd` values are normalised to slashes.
  if (/[/\-.]/.test(raw)) {
    const segments = raw.split(/[/\-.]/);
    const first = (segments[0] ?? '').replace(/\D/g, '');

    if (first.length === 4 && segments.length >= 3) {
      const year = first.slice(0, 4);
      const month = (segments[1] ?? '').replace(/\D/g, '').slice(0, 2);
      const day = (segments[2] ?? '').replace(/\D/g, '').slice(0, 2);
      return `${day}/${month}/${year}`;
    }

    const day = first.slice(0, 2);
    const monthFull = (segments[1] ?? '').replace(/\D/g, '');
    const month = monthFull.slice(0, 2);
    const overflow = monthFull.slice(2);
    const year = ((segments[2] ?? overflow).replace(/\D/g, '')).slice(0, 4);

    let out = day;
    if (segments.length > 1) {
      out += `/${month}`;
      if (segments.length > 2 || overflow) out += `/${year}`;
    }
    return out;
  }

  const digits = raw.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  if (digits.length <= 6) return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
}

/** Groups typed digits into `hh:mm`. */
function maskTimeText(raw: string): string {
  if (/[A-Za-z]/.test(raw)) return raw;
  const cleaned = raw.replace(/[^\d:]/g, '');
  if (cleaned.includes(':')) {
    const [hour, ...minute] = cleaned.split(':');
    const hours = hour.replace(/\D/g, '').slice(0, 2);
    const minutes = minute.join('').replace(/\D/g, '').slice(0, 2);
    return minutes ? `${hours}:${minutes}` : `${hours}:`;
  }
  const digits = cleaned.slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}:${digits.slice(2)}`;
}

/** Masks a typed date and optional time into `dd/mm/yy hh:mm`. */
function maskDateTimeText(raw: string): string {
  const spaceIndex = raw.indexOf(' ');
  const dateRaw = spaceIndex === -1 ? raw : raw.slice(0, spaceIndex);
  const timeRaw = spaceIndex === -1 ? '' : raw.slice(spaceIndex + 1);
  const maskedDate = maskDateText(dateRaw);
  if (spaceIndex === -1) return maskedDate;
  return `${maskedDate} ${maskTimeText(timeRaw)}`;
}

/* -------------------------------------------------------------------------- */
/* Live validation                                                            */
/* -------------------------------------------------------------------------- */

const MIN_YEAR = 1900;
const MAX_YEAR = 2100;

/**
 * True when the typed text is a complete, unambiguous date. Hand-typed values
 * are committed as soon as they reach this point, so a submit triggered by the
 * very next click (e.g. "Create") can never read the previous value from a
 * still-focused input.
 */
function isCompleteDateText(text: string): boolean {
  const value = text.trim();
  if (!value) return false;
  if (!parseTypedDate(value)) return false;
  if (/[A-Za-z]/.test(value)) return true;
  // Require a full year so `10/11/2` is not committed while still being typed.
  const parts = value.split(/[/.-]/);
  return parts.length === 3 && (parts[2]?.length ?? 0) >= 2;
}

/** True when the typed text holds both a complete date and a complete time. */
function isCompleteDateTimeText(text: string): boolean {
  const value = text.trim();
  if (!value) return false;
  if (!/\d{1,2}:\d{2}/.test(value)) return false;
  return parseTypedDateTime(value) !== null;
}

/**
 * Validates typed date text while the user is entering it. Returns `null`
 * while the value is still incomplete, or a human-readable error otherwise.
 */
function validateDateText(text: string): string | null {
  const value = text.trim();
  if (!value) return null;

  if (/[A-Za-z]/.test(value)) {
    const looksComplete =
      /^(\d{1,2}[\s/-]+[A-Za-z]{3,9}[\s,/-]+\d{2,4}|[A-Za-z]{3,9}[\s/-]+\d{1,2}[\s,/-]+\d{2,4})$/.test(value);
    if (!looksComplete) return null;
    return parseTypedDate(value) ? null : 'Enter a valid date as dd/mm/yy.';
  }

  const parts = value.split('/');
  if (parts.length < 3) return null;

  const [day, month, year] = parts;
  if (day === '') return null;
  if (!/^\d{1,2}$/.test(day)) return 'Day must be a number.';
  if (Number(day) < 1 || Number(day) > 31) return 'Day must be between 01 and 31.';

  if (month === '') return null;
  if (!/^\d{1,2}$/.test(month)) return 'Month must be a number.';
  if (Number(month) < 1 || Number(month) > 12) return 'Month must be between 01 and 12.';

  if (year === '') return null;
  if (!/^\d{2,4}$/.test(year)) return 'Year must be 2 or 4 digits.';
  const expandedYear = year.length <= 2 ? expandYear(year) : Number(year);
  if (expandedYear < MIN_YEAR || expandedYear > MAX_YEAR) {
    return `Year must be between ${MIN_YEAR} and ${MAX_YEAR}.`;
  }

  return parseTypedDate(value) ? null : "That day doesn't exist in the selected month.";
}

/** Validates typed time text (`hh:mm`) while the user is entering it. */
function validateTimeText(text: string): string | null {
  const value = text.trim();
  if (!value) return null;

  if (/[A-Za-z]/.test(value)) {
    const looksComplete = /^\d{1,2}:\d{2}\s*[ap]m?$/i.test(value);
    if (!looksComplete) return null;
    return parseTypedTime(value) ? null : 'Enter a valid time as hh:mm.';
  }

  const parts = value.split(':');
  if (parts.length < 2) return null;

  const [hour, minute] = parts;
  if (hour === '') return null;
  if (!/^\d{1,2}$/.test(hour)) return 'Hour must be a number.';
  if (Number(hour) > 23) return 'Hour must be between 00 and 23.';

  if (minute === '') return null;
  if (!/^\d{2}$/.test(minute)) return 'Minute must be 2 digits.';
  if (Number(minute) > 59) return 'Minute must be between 00 and 59.';

  return null;
}

/** Validates typed date + time text (`dd/mm/yy hh:mm`). */
function validateDateTimeText(text: string): string | null {
  const value = text.trim();
  if (!value) return null;

  const spaceIndex = value.indexOf(' ');
  if (spaceIndex === -1) return validateDateText(value);

  const dateError = validateDateText(value.slice(0, spaceIndex));
  if (dateError) return dateError;
  return validateTimeText(value.slice(spaceIndex + 1));
}

/* -------------------------------------------------------------------------- */
/* Popover                                                                    */
/* -------------------------------------------------------------------------- */

interface PickerPopoverProps {
  open: boolean;
  anchorRef: RefObject<HTMLDivElement | null>;
  onClose: () => void;
  children: ReactNode;
  align?: 'start' | 'end';
  label?: string;
}

function PickerPopover({ open, anchorRef, onClose, children, align = 'start', label = 'Date picker' }: PickerPopoverProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      setCoords(null);
      return;
    }

    const update = () => {
      const anchor = anchorRef.current;
      const panel = panelRef.current;
      if (!anchor || !panel) return;

      const rect = anchor.getBoundingClientRect();
      const panelRect = panel.getBoundingClientRect();
      const gap = 8;
      const margin = 8;

      const spaceBelow = window.innerHeight - rect.bottom - margin;
      const openAbove = spaceBelow < panelRect.height + gap && rect.top > panelRect.height + gap;
      const top = openAbove ? rect.top - panelRect.height - gap : rect.bottom + gap;

      let left = align === 'end' ? rect.right - panelRect.width : rect.left;
      left = Math.min(left, window.innerWidth - panelRect.width - margin);
      left = Math.max(margin, left);

      setCoords({ top: Math.max(margin, top), left });
    };

    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, anchorRef, align]);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (panelRef.current?.contains(target) || anchorRef.current?.contains(target)) return;
      onClose();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onClose, anchorRef]);

  if (!open) return null;

  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-label={label}
      tabIndex={-1}
      style={{
        position: 'fixed',
        top: coords?.top ?? 0,
        left: coords?.left ?? 0,
        visibility: coords ? 'visible' : 'hidden',
      }}
      className="z-[300] rounded-2xl border border-border bg-surface p-3 shadow-xl outline-none"
      onMouseDown={(event) => event.stopPropagation()}
    >
      {children}
    </div>,
    document.body,
  );
}

/* -------------------------------------------------------------------------- */
/* Calendar pieces                                                            */
/* -------------------------------------------------------------------------- */

function CalendarHeader({
  title,
  onPrevious,
  onNext,
}: {
  title: string;
  onPrevious: () => void;
  onNext: () => void;
}) {
  return (
    <div className="mb-2 flex items-center justify-between">
      <button
        type="button"
        onClick={onPrevious}
        aria-label="Previous month"
        className="flex size-8 items-center justify-center rounded-lg text-text-muted transition-colors hover:bg-background hover:text-text focus:outline-none focus:ring-2 focus:ring-primary/40"
      >
        <ChevronLeft size={17} />
      </button>
      <span className="text-sm font-semibold text-text">{title}</span>
      <button
        type="button"
        onClick={onNext}
        aria-label="Next month"
        className="flex size-8 items-center justify-center rounded-lg text-text-muted transition-colors hover:bg-background hover:text-text focus:outline-none focus:ring-2 focus:ring-primary/40"
      >
        <ChevronRight size={17} />
      </button>
    </div>
  );
}

function DayGrid({
  viewDate,
  selectedDate,
  min,
  max,
  onSelect,
}: {
  viewDate: Date;
  selectedDate: Date | null;
  min?: string;
  max?: string;
  onSelect: (date: Date) => void;
}) {
  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(viewDate), { weekStartsOn: 0 });
    const end = endOfWeek(endOfMonth(viewDate), { weekStartsOn: 0 });
    return eachDayOfInterval({ start, end });
  }, [viewDate]);

  const isDisabled = (date: Date) => {
    const iso = format(date, 'yyyy-MM-dd');
    return (!!min && iso < min) || (!!max && iso > max);
  };

  return (
    <div className="grid grid-cols-7 gap-y-0.5">
      {WEEKDAYS.map((day) => (
        <div key={day} className="flex h-8 items-center justify-center text-[0.6875rem] font-medium text-text-muted">
          {day}
        </div>
      ))}
      {days.map((day) => {
        const disabled = isDisabled(day);
        const selected = !!selectedDate && isSameDay(day, selectedDate);
        const outside = !isSameMonth(day, viewDate);
        const today = isToday(day);

        return (
          <button
            key={day.toISOString()}
            type="button"
            disabled={disabled}
            onClick={() => onSelect(day)}
            aria-label={format(day, 'EEEE, dd MMMM yyyy')}
            aria-pressed={selected}
            className={cn(
              'relative mx-auto flex size-9 items-center justify-center rounded-full text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50',
              outside && 'opacity-40',
              disabled
                ? 'cursor-not-allowed text-text-muted/50'
                : selected
                  ? 'bg-primary font-semibold text-white hover:bg-primary-dark'
                  : 'text-text hover:bg-primary/10',
              today && !selected && !disabled && 'bg-primary/10 font-semibold text-primary',
            )}
          >
            {format(day, 'd')}
            {today && !selected && (
              <span className="absolute bottom-1 left-1/2 size-1 -translate-x-1/2 rounded-full bg-primary" />
            )}
          </button>
        );
      })}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Trigger                                                                    */
/* -------------------------------------------------------------------------- */

interface TriggerProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange' | 'size' | 'min' | 'max'> {
  label?: ReactNode;
  error?: string;
  displayValue: string;
  placeholder: string;
  icon: ReactNode;
  open: boolean;
  onToggle: () => void;
  onClear: () => void;
  clearable: boolean;
  inputRef: RefObject<HTMLInputElement | null>;
  anchorRef: RefObject<HTMLDivElement | null>;
  className?: string;
  /** Allow the user to type a value by hand instead of only picking it. */
  editable?: boolean;
  /** Transforms the raw input while typing so digits auto-format (e.g. `dd/mm/yy`). */
  maskText?: (text: string) => string;
  /**
   * Validates the masked text on every keystroke. Return `null` while the value
   * is still incomplete, or a message to show as soon as the value is invalid.
   */
  validateText?: (text: string) => string | null;
  /**
   * Called with the raw text when the user commits a hand-typed value
   * (blur, Enter, or as soon as `isCompleteText` accepts it). Return
   * `{ ok: true }` when the text was accepted, or `{ ok: false, error }` to
   * surface a validation message.
   */
  onCommitText?: (text: string) => { ok: true } | { ok: false; error: string };
  /**
   * Predicate marking the typed text as complete. When supplied, a valid,
   * complete value is committed live while typing — a submit button clicked
   * straight after typing therefore reads the new value, not the old one.
   */
  isCompleteText?: (text: string) => boolean;
}

function PickerTrigger({
  displayValue,
  placeholder,
  icon,
  open,
  onToggle,
  onClear,
  clearable,
  inputRef,
  anchorRef,
  className,
  disabled,
  label,
  error,
  editable = false,
  maskText,
  validateText,
  onCommitText,
  isCompleteText,
  ...props
}: TriggerProps) {
  const [text, setText] = useState(displayValue);
  const [invalidText, setInvalidText] = useState('');
  const selectAllOnFocusRef = useRef(false);

  useEffect(() => {
    setText(displayValue);
    setInvalidText('');
  }, [displayValue]);

  const commitText = () => {
    if (!editable || !onCommitText) return;
    const trimmed = text.trim();
    if (trimmed === displayValue.trim()) {
      setInvalidText('');
      return;
    }
    if (!trimmed) {
      if (displayValue && clearable) {
        const result = onCommitText('');
        if (result.ok) {
          setText('');
          setInvalidText('');
        } else {
          setInvalidText(result.error);
        }
      } else {
        setText(displayValue);
        setInvalidText('');
      }
      return;
    }
    const result = onCommitText(trimmed);
    if (result.ok) {
      setInvalidText('');
      setText(displayValue);
    } else {
      setInvalidText(result.error);
    }
  };

  const shownError = error || invalidText;
  const shownValue = editable ? text : displayValue;

  return (
    <div className={label || shownError ? 'w-full' : 'contents'}>
      {label && <label className="mb-1 block text-sm font-medium text-text">{label}</label>}
      <div
        ref={anchorRef}
        className={cn(
          'group relative flex w-full items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 text-sm text-text transition-colors',
          !disabled && 'cursor-pointer hover:border-primary/40',
          open && 'border-primary ring-2 ring-primary/20',
          disabled && 'cursor-not-allowed opacity-50',
          shownError && 'border-danger',
          className,
        )}
        onClick={(event) => {
          if (disabled) return;
          // Let the user click into the field to type without toggling the popover.
          if (editable && event.target === inputRef.current) return;
          onToggle();
        }}
      >
        <span className="pointer-events-none shrink-0 text-text-muted">{icon}</span>
        <input
          {...props}
          ref={inputRef}
          type="text"
          readOnly={!editable}
          disabled={disabled}
          value={shownValue}
          placeholder={placeholder}
          aria-haspopup="dialog"
          aria-expanded={open}
          autoComplete="off"
          spellCheck={false}
          onFocus={
            editable
              ? (event) => {
                  if (event.currentTarget.value) {
                    selectAllOnFocusRef.current = true;
                    event.currentTarget.select();
                  }
                }
              : undefined
          }
          onMouseUp={
            editable
              ? (event) => {
                  if (selectAllOnFocusRef.current) {
                    event.preventDefault();
                    selectAllOnFocusRef.current = false;
                  }
                }
              : undefined
          }
          onChange={
            editable
              ? (event) => {
                  const next = maskText ? maskText(event.target.value) : event.target.value;
                  setText(next);
                  const error = validateText && next.trim() ? validateText(next) ?? '' : '';
                  setInvalidText(error);
                  // Commit complete, valid input immediately. Waiting for blur
                  // lets a submit click that follows the last keystroke read the
                  // previous value from the parent's state.
                  if (!error && next.trim() && onCommitText && isCompleteText?.(next)) {
                    const result = onCommitText(next);
                    if (!result.ok) setInvalidText(result.error);
                  }
                }
              : undefined
          }
          onBlur={editable ? commitText : undefined}
          onKeyDown={(event) => {
            if (!editable) {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                if (!disabled) onToggle();
              }
              return;
            }
            const dirty = text.trim() !== displayValue.trim();
            if (event.key === 'Enter') {
              if (dirty || open) {
                event.preventDefault();
                commitText();
                if (open) onToggle();
              }
            } else if (event.key === 'Escape') {
              if (dirty) {
                event.preventDefault();
                setText(displayValue);
                setInvalidText('');
              }
            } else if (event.key === 'ArrowDown') {
              event.preventDefault();
              if (!open) onToggle();
            }
          }}
          className={cn(
            'min-w-0 flex-1 rounded bg-transparent text-sm text-text placeholder:text-text-muted focus:outline-none disabled:cursor-not-allowed',
            editable ? 'cursor-text' : 'cursor-pointer',
          )}
        />
        {clearable && shownValue && !disabled && (
          <button
            type="button"
            tabIndex={-1}
            aria-label="Clear"
            onClick={(event) => {
              event.stopPropagation();
              setText('');
              setInvalidText('');
              onClear();
            }}
            className="flex size-5 shrink-0 items-center justify-center rounded-full text-text-muted transition-colors hover:bg-border/60 hover:text-text focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            <X size={13} />
          </button>
        )}
      </div>
      {shownError && <p className="mt-1 text-xs text-danger">{shownError}</p>}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Date picker                                                                */
/* -------------------------------------------------------------------------- */

export interface DatePickerProps extends Omit<TriggerProps, 'displayValue' | 'placeholder' | 'icon' | 'open' | 'onToggle' | 'onClear' | 'clearable' | 'inputRef' | 'anchorRef'> {
  value?: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
  placeholder?: string;
  clearable?: boolean;
  align?: 'start' | 'end';
  /**
   * IANA timezone the picker's "Today" shortcut and initial calendar month
   * follow (Settings → General). Without it they use the browser's zone, which
   * can be a different calendar day than the hospital's.
   */
  timeZone?: string;
}

export function DatePicker({
  value,
  onChange,
  min,
  max,
  placeholder = 'dd/mm/yy',
  clearable = true,
  align = 'start',
  className,
  disabled,
  required,
  timeZone,
  ...props
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const selectedDate = parseDate(value);
  const [viewDate, setViewDate] = useState<Date>(
    () => selectedDate ?? parseDate(nowDateTimeLocalInTimeZone(timeZone).date) ?? new Date(),
  );
  const anchorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const openPicker = () => {
    setViewDate(
      selectedDate ?? parseDate(nowDateTimeLocalInTimeZone(timeZone).date) ?? new Date(),
    );
    setOpen(true);
  };

  const close = () => {
    setOpen(false);
    inputRef.current?.focus();
  };

  return (
    <>
      <PickerTrigger
        {...props}
        required={required}
        disabled={disabled}
        inputRef={inputRef}
        anchorRef={anchorRef}
        className={className}
        displayValue={formatDateLabel(value)}
        placeholder={placeholder}
        icon={<CalendarDays size={16} />}
        open={open}
        onToggle={() => (open ? close() : openPicker())}
        onClear={() => {
          onChange('');
          close();
        }}
        clearable={clearable}
        editable
        maskText={maskDateText}
        validateText={validateDateText}
        isCompleteText={isCompleteDateText}
        onCommitText={(raw) => {
          if (!raw.trim()) {
            onChange('');
            return { ok: true };
          }
          const iso = parseTypedDate(raw);
          if (!iso) {
            return { ok: false, error: validateDateText(raw) ?? 'Enter a valid date as dd/mm/yy.' };
          }
          if (min && iso < min) {
            return { ok: false, error: `Date must be on or after ${formatDateLabel(min)}.` };
          }
          if (max && iso > max) {
            return { ok: false, error: `Date must be on or before ${formatDateLabel(max)}.` };
          }
          onChange(iso);
          return { ok: true };
        }}
      />

      <PickerPopover open={open} anchorRef={anchorRef} onClose={close} align={align} label="Choose a date">
        <CalendarHeader
          title={format(viewDate, 'MMMM yyyy')}
          onPrevious={() => setViewDate(subMonths(viewDate, 1))}
          onNext={() => setViewDate(addMonths(viewDate, 1))}
        />
        <DayGrid
          viewDate={viewDate}
          selectedDate={selectedDate}
          min={min}
          max={max}
          onSelect={(date) => {
            onChange(format(date, 'yyyy-MM-dd'));
            close();
          }}
        />
        <div className="mt-3 flex items-center justify-between border-t border-border pt-3">
          <button
            type="button"
            onClick={() => {
              onChange('');
              close();
            }}
            className="rounded-lg px-2 py-1.5 text-xs font-medium text-text-muted transition-colors hover:text-text focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            Clear
          </button>
          <button
            type="button"
            onClick={() => {
              onChange(nowDateTimeLocalInTimeZone(timeZone).date);
              close();
            }}
            className="rounded-lg px-2 py-1.5 text-xs font-medium text-primary transition-colors hover:bg-primary/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            Today
          </button>
        </div>
      </PickerPopover>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Date & time picker                                                         */
/* -------------------------------------------------------------------------- */

function TimeSelects({
  time,
  onChange,
}: {
  time: string;
  onChange: (value: string) => void;
}) {
  const normalized = toTimeInputValue(time);
  const [rawHour, rawMinute] = normalized ? normalized.split(':').map(Number) : [0, 0];
  const hour12 = rawHour % 12 || 12;
  const period = rawHour >= 12 ? 'PM' : 'AM';
  const minute = rawMinute || 0;

  const emit = (nextHour12: number, nextMinute: number, nextPeriod: string) => {
    const hour24 = (nextHour12 % 12) + (nextPeriod === 'PM' ? 12 : 0);
    onChange(`${String(hour24).padStart(2, '0')}:${String(nextMinute).padStart(2, '0')}`);
  };

  const selectClass =
    'rounded-lg border border-border bg-background px-2 py-1.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/30';

  return (
    <div className="flex items-center gap-1.5">
      <select
        aria-label="Hour"
        value={hour12}
        onChange={(event) => emit(Number(event.target.value), minute, period)}
        className={selectClass}
      >
        {Array.from({ length: 12 }, (_, index) => index + 1).map((hour) => (
          <option key={hour} value={hour}>
            {String(hour).padStart(2, '0')}
          </option>
        ))}
      </select>
      <span className="text-text-muted">:</span>
      <select
        aria-label="Minute"
        value={minute}
        onChange={(event) => emit(hour12, Number(event.target.value), period)}
        className={selectClass}
      >
        {Array.from({ length: 60 }, (_, index) => index).map((value) => (
          <option key={value} value={value}>
            {String(value).padStart(2, '0')}
          </option>
        ))}
      </select>
      <select
        aria-label="AM or PM"
        value={period}
        onChange={(event) => emit(hour12, minute, event.target.value)}
        className={selectClass}
      >
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
  );
}

export interface DateTimePickerProps extends Omit<DatePickerProps, 'value' | 'onChange' | 'placeholder' | 'icon'> {
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /**
   * IANA timezone the picker works in (e.g. `Asia/Dhaka`). Used by the "Now"
   * shortcut and the initial calendar month so both follow the hospital
   * timezone configured on Settings → General instead of the browser's zone.
   */
  timeZone?: string;
}

export function DateTimePicker({
  value,
  onChange,
  min,
  max,
  placeholder = 'dd/mm/yy hh:mm',
  clearable = true,
  align = 'start',
  className,
  disabled,
  required,
  timeZone,
  ...props
}: DateTimePickerProps) {
  const [open, setOpen] = useState(false);
  const datePart = toDateInputValue(value);
  const timePart = toTimeInputValue(String(value ?? '').split('T')[1] ?? '');
  const selectedDate = parseDate(datePart);
  const [viewDate, setViewDate] = useState<Date>(
    () => selectedDate ?? parseDate(nowDateTimeLocalInTimeZone(timeZone).date) ?? new Date(),
  );
  const anchorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const emit = (nextDate: string, nextTime: string) => {
    const time = nextTime || '00:00';
    onChange(`${nextDate}T${time}`);
  };

  const openPicker = () => {
    setViewDate(selectedDate ?? parseDate(nowDateTimeLocalInTimeZone(timeZone).date) ?? new Date());
    setOpen(true);
  };

  const close = () => {
    setOpen(false);
    inputRef.current?.focus();
  };

  return (
    <>
      <PickerTrigger
        {...props}
        required={required}
        disabled={disabled}
        inputRef={inputRef}
        anchorRef={anchorRef}
        className={className}
        displayValue={formatDateTimeLabel(value)}
        placeholder={placeholder}
        icon={<CalendarDays size={16} />}
        open={open}
        onToggle={() => (open ? close() : openPicker())}
        onClear={() => {
          onChange('');
          close();
        }}
        clearable={clearable}
        editable
        maskText={maskDateTimeText}
        validateText={validateDateTimeText}
        isCompleteText={isCompleteDateTimeText}
        onCommitText={(raw) => {
          if (!raw.trim()) {
            onChange('');
            return { ok: true };
          }
          const parsed = parseTypedDateTime(raw);
          if (!parsed) {
            return {
              ok: false,
              error: validateDateTimeText(raw) ?? 'Enter a valid date and time as dd/mm/yy hh:mm.',
            };
          }
          const [date, time] = parsed.split('T');
          if (min && date < min) {
            return { ok: false, error: `Date must be on or after ${formatDateLabel(min)}.` };
          }
          if (max && date > max) {
            return { ok: false, error: `Date must be on or before ${formatDateLabel(max)}.` };
          }
          onChange(`${date}T${time || timePart || '00:00'}`);
          return { ok: true };
        }}
      />

      <PickerPopover open={open} anchorRef={anchorRef} onClose={close} align={align} label="Choose a date and time">
        <CalendarHeader
          title={format(viewDate, 'MMMM yyyy')}
          onPrevious={() => setViewDate(subMonths(viewDate, 1))}
          onNext={() => setViewDate(addMonths(viewDate, 1))}
        />
        <DayGrid
          viewDate={viewDate}
          selectedDate={selectedDate}
          min={min}
          max={max}
          onSelect={(date) => {
            const nextDate = format(date, 'yyyy-MM-dd');
            emit(nextDate, timePart);
            setViewDate(date);
          }}
        />

        <div className="mt-3 border-t border-border pt-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="flex items-center gap-1.5 text-xs font-medium text-text-muted">
              <Clock size={13} /> Time
            </span>
            <button
              type="button"
              onClick={() => {
                const now = nowDateTimeLocalInTimeZone(timeZone);
                emit(datePart || now.date, now.time);
              }}
              className="rounded-lg px-2 py-1 text-xs font-medium text-primary transition-colors hover:bg-primary/10"
            >
              Now
            </button>
          </div>
          <TimeSelects
            time={timePart || '00:00'}
            onChange={(nextTime) => emit(datePart || format(viewDate, 'yyyy-MM-dd'), nextTime)}
          />
        </div>

        <div className="mt-3 flex items-center justify-between border-t border-border pt-3">
          <button
            type="button"
            onClick={() => {
              onChange('');
              close();
            }}
            className="rounded-lg px-2 py-1.5 text-xs font-medium text-text-muted transition-colors hover:text-text"
          >
            Clear
          </button>
          <button
            type="button"
            onClick={close}
            className="rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-primary-dark"
          >
            Done
          </button>
        </div>
      </PickerPopover>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Month picker                                                               */
/* -------------------------------------------------------------------------- */

const MONTHS = Array.from({ length: 12 }, (_, index) => index);

export interface MonthPickerProps extends Omit<DatePickerProps, 'value' | 'onChange' | 'placeholder' | 'icon' | 'min' | 'max'> {
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  min?: string;
  max?: string;
}

export function MonthPicker({
  value,
  onChange,
  min,
  max,
  placeholder = 'Select month',
  clearable = true,
  align = 'start',
  className,
  disabled,
  required,
  ...props
}: MonthPickerProps) {
  const [open, setOpen] = useState(false);
  const selectedMonth = parseMonth(value);
  const [viewYear, setViewYear] = useState<number>(() => selectedMonth?.getFullYear() ?? new Date().getFullYear());
  const anchorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const close = () => {
    setOpen(false);
    inputRef.current?.focus();
  };

  const openPicker = () => {
    setViewYear(selectedMonth?.getFullYear() ?? new Date().getFullYear());
    setOpen(true);
  };

  const isDisabled = (month: number) => {
    const value = `${viewYear}-${String(month + 1).padStart(2, '0')}`;
    return (!!min && value < min) || (!!max && value > max);
  };

  return (
    <>
      <PickerTrigger
        {...props}
        required={required}
        disabled={disabled}
        inputRef={inputRef}
        anchorRef={anchorRef}
        className={className}
        displayValue={formatMonthLabel(value)}
        placeholder={placeholder}
        icon={<CalendarDays size={16} />}
        open={open}
        onToggle={() => (open ? close() : openPicker())}
        onClear={() => {
          onChange('');
          close();
        }}
        clearable={clearable}
      />

      <PickerPopover open={open} anchorRef={anchorRef} onClose={close} align={align} label="Choose a month">
        <div className="mb-2 flex items-center justify-between">
          <button
            type="button"
            onClick={() => setViewYear((year) => year - 1)}
            aria-label="Previous year"
            className="flex size-8 items-center justify-center rounded-lg text-text-muted transition-colors hover:bg-background hover:text-text focus:outline-none focus:ring-2 focus:ring-primary/40"
          >
            <ChevronLeft size={17} />
          </button>
          <span className="text-sm font-semibold text-text">{viewYear}</span>
          <button
            type="button"
            onClick={() => setViewYear((year) => year + 1)}
            aria-label="Next year"
            className="flex size-8 items-center justify-center rounded-lg text-text-muted transition-colors hover:bg-background hover:text-text focus:outline-none focus:ring-2 focus:ring-primary/40"
          >
            <ChevronRight size={17} />
          </button>
        </div>

        <div className="grid grid-cols-3 gap-1">
          {MONTHS.map((month) => {
            const monthValue = `${viewYear}-${String(month + 1).padStart(2, '0')}`;
            const selected =
              selectedMonth?.getFullYear() === viewYear && selectedMonth?.getMonth() === month;
            const current =
              new Date().getFullYear() === viewYear && new Date().getMonth() === month;
            const disabled = isDisabled(month);

            return (
              <button
                key={month}
                type="button"
                disabled={disabled}
                aria-pressed={selected}
                onClick={() => {
                  onChange(monthValue);
                  close();
                }}
                className={cn(
                  'rounded-lg px-2 py-2.5 text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50',
                  disabled
                    ? 'cursor-not-allowed text-text-muted/50'
                    : selected
                      ? 'bg-primary font-semibold text-white hover:bg-primary-dark'
                      : 'text-text hover:bg-primary/10',
                  current && !selected && !disabled && 'bg-primary/10 font-semibold text-primary',
                )}
              >
                {format(new Date(viewYear, month, 1), 'MMM')}
              </button>
            );
          })}
        </div>

        <div className="mt-3 flex items-center justify-between border-t border-border pt-3">
          <button
            type="button"
            onClick={() => {
              onChange('');
              close();
            }}
            className="rounded-lg px-2 py-1.5 text-xs font-medium text-text-muted transition-colors hover:text-text"
          >
            Clear
          </button>
          <button
            type="button"
            onClick={() => {
              const now = new Date();
              onChange(format(now, 'yyyy-MM'));
              close();
            }}
            className="rounded-lg px-2 py-1.5 text-xs font-medium text-primary transition-colors hover:bg-primary/10"
          >
            This month
          </button>
        </div>
      </PickerPopover>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Time picker                                                                */
/* -------------------------------------------------------------------------- */

export interface TimePickerProps extends Omit<DatePickerProps, 'value' | 'onChange' | 'placeholder' | 'icon' | 'min' | 'max'> {
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

export function TimePicker({
  value,
  onChange,
  placeholder = 'Select time',
  clearable = true,
  align = 'start',
  className,
  disabled,
  required,
  ...props
}: TimePickerProps) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const close = () => {
    setOpen(false);
    inputRef.current?.focus();
  };

  return (
    <>
      <PickerTrigger
        {...props}
        required={required}
        disabled={disabled}
        inputRef={inputRef}
        anchorRef={anchorRef}
        className={className}
        displayValue={formatTimeLabel(value)}
        placeholder={placeholder}
        icon={<Clock size={16} />}
        open={open}
        onToggle={() => (open ? close() : setOpen(true))}
        onClear={() => {
          onChange('');
          close();
        }}
        clearable={clearable}
      />

      <PickerPopover open={open} anchorRef={anchorRef} onClose={close} align={align} label="Choose a time">
        <div className="px-1 py-1">
          <TimeSelects
            time={toTimeInputValue(value) || '00:00'}
            onChange={(next) => onChange(next)}
          />
        </div>
        <div className="mt-2 flex items-center justify-between border-t border-border pt-3">
          <button
            type="button"
            onClick={() => {
              onChange('');
              close();
            }}
            className="rounded-lg px-2 py-1.5 text-xs font-medium text-text-muted transition-colors hover:text-text"
          >
            Clear
          </button>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => onChange(format(new Date(), 'HH:mm'))}
              className="rounded-lg px-2 py-1.5 text-xs font-medium text-primary transition-colors hover:bg-primary/10"
            >
              Now
            </button>
            <button
              type="button"
              onClick={close}
              className="rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-primary-dark"
            >
              Done
            </button>
          </div>
        </div>
      </PickerPopover>
    </>
  );
}
